/**
 * Stage1_Text - 文案创作阶段（两阶段故事创建版）
 *
 * 用户旅程第二步：AI生成故事手稿，用户可预览、查看角色卡、追踪性格弧线，并编辑
 * 路由: /create/stage/1?storyId=xxx
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { StepIndicator } from '../../components/create/StepIndicator';
import {
  Loader2, Sparkles, RefreshCw, ChevronRight, ChevronLeft,
  BookOpen, Clock, CheckCircle, User, PawPrint, FileText,
  Users, Heart, MapPin, X, Check
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// =============================================================================
// 类型定义
// =============================================================================

interface StoryPage {
  pageNumber: number;
  text: string;
  illustrationPrompt: string;
  supportingCharacters?: Array<{
    name: string;
    appearance: string;
    action?: string;
    position?: string;
  }>;
}

interface ManuscriptProtagonist {
  name: string;
  appearance: string;
  personality: string;
  traits: string[];
  interests: string[];
}

interface ManuscriptSupportingCharacter {
  name: string;
  species: string;
  appearance: string;
  personality: string;
  role: string;
  firstAppearancePage: number;
  typicalActions: string[];
  relationshipToProtagonist: string;
}

interface StoryArcEntry {
  page: number;
  sceneSummary: string;
  charactersPresent: string[];
  keyAction: string;
  setting: string;
  emotionalTone: string;
  traitDemonstrated: string;
}

interface Manuscript {
  title: string;
  theme: string;
  moral: string;
  protagonist: ManuscriptProtagonist;
  supportingCharacters: ManuscriptSupportingCharacter[];
  storyArc: StoryArcEntry[];
}

interface SupportingCharacter {
  id: string;
  storyId: string;
  characterId: string;
  roleInStory?: string;
  character: {
    id: string;
    name: string;
    description?: string;
    type: string;
    appearsInPages: number[];
    seed?: string;
    referenceImage?: string;
  };
}

interface StoryData {
  id: string;
  title: string;
  stage: string;
  pageCount: number;
  targetAge: string;
  interests: string[];
  artStyle: string;
  theme: string;
  config?: {
    characterType?: string;
    gender?: string;
    outline?: string;
    characterName?: string;
    appearance?: string;
  };
  textContent?: {
    pages: StoryPage[];
    manuscript?: Manuscript;
  };
  characters?: SupportingCharacter[];
  creationMode?: string;
}

// =============================================================================
// 组件
// =============================================================================

export default function Stage1_Text() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('storyId');

  const [story, setStory] = useState<StoryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  // 【防重】同步锁，防止快速双击导致重复请求
  const isSubmittingRef = useRef(false);
  const [editedPages, setEditedPages] = useState<StoryPage[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [manuscript, setManuscript] = useState<Manuscript | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    if (!storyId) {
      navigate('/create');
      return;
    }
    fetchStory();
  }, [storyId]);

  const fetchStory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}`);
      const result = await response.json();
      const data = result.data as StoryData;
      setStory(data);

      if (data.textContent?.pages) {
        setEditedPages(data.textContent.pages);
      }

      if (data.textContent?.manuscript) {
        setManuscript(data.textContent.manuscript);
      }

      // 控制台输出故事配置信息
      console.log('[Stage1_Text] Story config:', {
        title: data.title,
        theme: data.theme,
        targetAge: data.targetAge,
        pageCount: data.pageCount,
        artStyle: data.artStyle,
        interests: data.interests,
        outline: data.config?.outline,
        characterName: data.config?.characterName,
        characterType: data.config?.characterType,
        appearance: data.config?.appearance,
      });

      // 控制台输出文案信息
      if (data.textContent?.pages) {
        console.log('[Stage1_Text] Story text content:', data.textContent.pages.map((p: StoryPage) => ({
          pageNumber: p.pageNumber,
          text: p.text,
          illustrationPrompt: p.illustrationPrompt,
        })));
      }

      // 控制台输出辅助角色信息
      const supportingChars = data.characters?.filter(
        (c: SupportingCharacter) => c.character.type !== 'protagonist'
      ) || [];
      if (supportingChars.length > 0) {
        console.log('[Stage1_Text] Supporting characters:', supportingChars.map((c: SupportingCharacter) => ({
          name: c.character.name,
          type: c.character.type,
          appearance: c.character.description,
          appearsInPages: c.character.appearsInPages,
          roleInStory: c.roleInStory,
        })));
      } else {
        console.log('[Stage1_Text] No supporting characters found');
      }
    } catch (error) {
      showNotification('error', '获取故事失败');
      navigate('/create');
    } finally {
      setIsLoading(false);
    }
  };

  const generateText = async () => {
    // 【防重】同步锁，拦截重复点击（比 state 更早生效）
    if (isSubmittingRef.current) {
      console.log('[Stage1] Double click blocked by ref lock');
      return;
    }

    // 【防误触】如果已有文案，二次确认避免覆盖下游内容
    const hasContentBefore = (editedPages || []).some((p: any) => p?.text || p?.content);
    if (hasContentBefore) {
      const confirmed = window.confirm(
        '⚠️ 重新生成文案将覆盖当前所有页面内容\n\n' +
        '已生成的音频和插图与新文案内容可能不匹配，需要重新生成。\n\n' +
        '确定要重新生成吗？'
      );
      if (!confirmed) return;
    }

    isSubmittingRef.current = true;
    setIsGenerating(true);

    try {
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/1/text`, {
        method: 'POST',
      });
      const result = await response.json();

      if (result.success) {
        setEditedPages(result.data.pages);
        if (result.data.manuscript) {
          setManuscript(result.data.manuscript);
        }
        setHasChanges(false);
        showNotification('success', '文案生成成功！');
        await fetchStory();
      } else {
        showNotification('error', '生成失败: ' + result.error);
      }
    } catch (error) {
      showNotification('error', '生成失败');
    } finally {
      isSubmittingRef.current = false;
      setIsGenerating(false);
    }
  };

  const saveText = async () => {
    try {
      await fetch(`${API_BASE_URL}/stories/${storyId}/stage/1/text`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pages: editedPages }),
      });
      setHasChanges(false);
      showNotification('success', '保存成功！');
    } catch (error) {
      showNotification('error', '保存失败');
    }
  };

  const goToPrevStage = () => {
    if (story?.creationMode === 'DIY_MANUAL') {
      navigate(`/create/diy/editor/${storyId}`);
    } else {
      navigate(`/create?storyId=${storyId}&returnStep=3&flow=ai`);
    }
  };

  const goToNextStage = async () => {
    if (hasChanges) {
      await saveText();
    }
    try {
      await fetch(`${API_BASE_URL}/stories/${storyId}/stage/1/next`, {
        method: 'POST',
      });
      navigate(`/create/stage/2?storyId=${storyId}`);
    } catch (error) {
      showNotification('error', '进入下一阶段失败');
    }
  };

  const updatePageText = (index: number, text: string) => {
    const newPages = [...editedPages];
    newPages[index] = { ...newPages[index], text };
    setEditedPages(newPages);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const hasGeneratedText = editedPages.length > 0;
  const currentStep = 1;

  // 主角信息
  const protagonist = manuscript?.protagonist;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8">
      <div className="w-full flex justify-center">
        <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col gap-6">
          {/* 步骤导航 */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 w-full">
            <StepIndicator currentStep={currentStep} />
          </motion.div>

          {/* Toast 通知 */}
          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-sm font-medium ${
                notification.type === 'success'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-rose-500 text-white'
              }`}
            >
              {notification.type === 'success' ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {notification.message}
            </motion.div>
          )}

          {/* 页面标题 -->
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {hasGeneratedText ? '预览并编辑文案' : '创作故事文案'}
            </h1>
            <p className="text-gray-600">
              {hasGeneratedText
                ? 'AI已生成故事手稿，你可以预览角色、追踪性格弧线并编辑任意页面'
                : 'AI将根据你的配置创作英文故事手稿，约需2-3分钟'}
            </p>
          </motion.div>

          {/* 配置摘要 */}
          {story && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
              <Card className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
                <div className="flex flex-wrap items-center text-sm gap-x-4 gap-y-2">
                  <div className="flex items-center gap-1.5">
                    <BookOpen className="w-4 h-4 text-blue-500" />
                    <span className="text-slate-700">{story.pageCount}页</span>
                  </div>
                  <span className="text-slate-300">|</span>
                  <span className="text-slate-700">{story.targetAge}岁</span>
                  <span className="text-slate-300">|</span>
                  <span className="text-slate-700 truncate max-w-[150px]">{story.theme}</span>
                  {story.config?.characterType && (
                    <>
                      <span className="text-slate-300">|</span>
                      <div className="flex items-center gap-1.5">
                        {story.config.characterType === 'human' ? (
                          <User className="w-4 h-4 text-indigo-500" />
                        ) : (
                          <PawPrint className="w-4 h-4 text-amber-500" />
                        )}
                        <span className="text-slate-700">
                          {story.config.characterType === 'human' && story.config.gender
                            ? (story.config.gender === 'boy' ? '男' : '女')
                            : (story.config.characterType === 'human' ? '人物' : '动物')}
                        </span>
                      </div>
                    </>
                  )}
                  {story.config?.characterName && (
                    <>
                      <span className="text-slate-300">|</span>
                      <span className="text-slate-700 font-medium">{story.config.characterName}</span>
                    </>
                  )}
                </div>
                {/* 故事梗概 */}
                {story.config?.outline && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-start gap-2">
                    <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-500 line-clamp-2">{story.config.outline}</p>
                  </div>
                )}
              </Card>
            </motion.div>
          )}

          {/* 主要内容区 */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            {!hasGeneratedText ? (
              <Card className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mb-4">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-xl font-semibold mb-2">开始生成故事文案</h2>
                <p className="text-gray-600 mb-4 max-w-[500px]">
                  AI将根据你选择的兴趣爱好和主题，创作一个{story?.pageCount}页的英文绘本故事手稿
                </p>
                <div className="flex items-center text-sm text-gray-500 gap-2 mb-6">
                  <Clock className="w-4 h-4" />
                  <span>预计耗时 2-3 分钟</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="secondary"
                    onClick={goToPrevStage}
                    className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    上一步
                  </Button>
                  <Button
                    onClick={generateText}
                    disabled={isGenerating}
                    className="bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                  >
                    {isGenerating ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin inline" />生成中...</>
                    ) : (
                      <><Sparkles className="w-5 h-5 mr-2 inline" />开始生成</>
                    )}
                  </Button>
                </div>
              </Card>
            ) : (
              <div className="flex flex-col gap-6">
                {/* 故事标题 */}
                <Card className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-6 h-6 text-amber-600" />
                    <div>
                      <div className="text-sm text-amber-600 font-medium">故事标题</div>
                      <div className="text-xl font-bold text-gray-900">{story?.title || '未命名故事'}</div>
                    </div>
                  </div>
                  {manuscript?.moral && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-start gap-2">
                      <Heart className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-500">{manuscript.moral}</p>
                    </div>
                  )}
                </Card>

                {/* 主角卡片 */}
                {protagonist && (
                  <Card className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-indigo-500" />
                      主角
                    </h3>
                    <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                      <div className="font-semibold text-indigo-900 text-lg">{protagonist.name}</div>
                      <div className="text-sm text-indigo-700 mt-1">{protagonist.appearance}</div>
                      <div className="text-sm text-indigo-600 mt-1">{protagonist.personality}</div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {protagonist.traits?.map(trait => (
                          <span key={trait} className="px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                            {trait}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Card>
                )}

                {/* 配角卡片 */}
                {manuscript?.supportingCharacters && manuscript.supportingCharacters.length > 0 && (
                  <Card className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-emerald-500" />
                      配角
                      <span className="text-sm font-normal text-slate-400">
                        ({manuscript.supportingCharacters.length}位)
                      </span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {manuscript.supportingCharacters.map(char => (
                        <div
                          key={char.name}
                          className="bg-slate-50 rounded-xl p-4 border border-slate-100 hover:border-emerald-200 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-semibold text-gray-900">{char.name}</div>
                            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                              {char.species}
                            </span>
                          </div>
                          <div className="text-sm text-slate-600 mt-1 line-clamp-3">
                            {char.appearance}
                          </div>
                          <div className="text-xs text-slate-400 mt-2 flex items-center gap-3">
                            <span>角色: {char.role}</span>
                            <span>首次出场: 第{char.firstAppearancePage}页</span>
                          </div>
                          {char.typicalActions && char.typicalActions.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {char.typicalActions.map((action, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded"
                                >
                                  {action}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Trait Tracker */}
                {manuscript?.storyArc && manuscript.storyArc.some(a => a.traitDemonstrated) && (
                  <Card className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-amber-500" />
                      性格追踪 (Trait Tracker)
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {manuscript.storyArc.map(arc => (
                        arc.traitDemonstrated && (
                          <span
                            key={arc.page}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-sm border border-amber-100"
                          >
                            <span className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold">
                              {arc.page}
                            </span>
                            {arc.traitDemonstrated}
                          </span>
                        )
                      ))}
                    </div>
                  </Card>
                )}

                {/* 页面列表 */}
                <div className="flex flex-col gap-6">
                  {editedPages.map((page, index) => (
                    <Card key={page.pageNumber} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      <div className="bg-slate-50 border-b border-slate-100 flex items-center justify-between px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                            {page.pageNumber}
                          </span>
                          <span className="text-sm text-gray-500">第 {page.pageNumber} 页</span>
                          {/* 该页体现的性格标签 */}
                          {manuscript?.storyArc[index]?.traitDemonstrated && (
                            <span className="ml-2 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
                              {manuscript.storyArc[index].traitDemonstrated}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-slate-500 bg-slate-100 rounded-lg px-3 py-1">
                          {page.text?.split(' ').length || 0} 词
                        </span>
                      </div>
                      <div className="p-4 sm:p-6">
                        <textarea
                          value={page.text}
                          onChange={(e) => updatePageText(index, e.target.value)}
                          className="w-full min-h-[96px] p-3 border border-slate-200 rounded-xl resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="输入页面文案..."
                        />
                        {/* 插画提示词预览 */}
                        {page.illustrationPrompt && (
                          <div className="mt-3 text-xs text-slate-400 bg-slate-50 rounded-lg p-2">
                            <span className="font-medium text-slate-500">插画提示:</span>
                            <p className="mt-1 leading-relaxed">{page.illustrationPrompt}</p>
                          </div>
                        )}
                        {/* 该页出场的配角 */}
                        {page.supportingCharacters && page.supportingCharacters.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {page.supportingCharacters.map(sc => (
                              <span
                                key={sc.name}
                                className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5"
                              >
                                {sc.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>

                {/* 操作按钮 */}
                <div className="flex items-center justify-between gap-2 pt-6">
                  <Button
                    variant="secondary"
                    onClick={goToPrevStage}
                    className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 whitespace-nowrap shrink-0"
                  >
                    <ChevronLeft className="w-4 h-4 shrink-0" />
                    上一步
                  </Button>

                  <div className="flex items-center gap-3 shrink-0">
                    <Button
                      onClick={generateText}
                      disabled={isGenerating}
                      className="bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 whitespace-nowrap shrink-0"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 shrink-0 ${isGenerating ? 'animate-spin' : ''}`} />
                      重新生成
                    </Button>
                    <Button
                      onClick={goToNextStage}
                      className="bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 whitespace-nowrap shrink-0"
                    >
                      <CheckCircle className="w-4 h-4 mr-2 shrink-0" />
                      下一步
                      <ChevronRight className="w-4 h-4 ml-2 shrink-0" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
